import clsx from "clsx"
import { Ref, forwardRef, useState } from "react"

import useDebouncedCallback from "@hooks/useDebouncedCallback"
import useIsDesktop from "@hooks/useIsDesktop"

import { faIcon } from "@utils/faIcon"

import { TruncatableText } from "@particles"

import { Icon, Tooltip } from "@atoms"

import styles from "./FilterChipBox.module.scss"
import { FilterChipBoxProps } from "./FilterChipBox.types"

const CHEVRON_ICON = faIcon({ name: "angle-down", style: "solid" })
const REMOVE_ICON = faIcon({ name: "xmark", style: "regular" })
const REMOVE_LABEL = "Remove"

function FilterChipBox(props: FilterChipBoxProps, ref: Ref<HTMLElement>) {
    const {
        variant = "content",
        className,

        children,
        slotLeft,

        isFullWidth = false,

        onClick,
        noDebounce = false,

        _isFocused = false,
        _isHovered = false,
        isPressed = false,
        isDisabled = false,
        isWarning = false,

        ...rest
    } = props

    const isDesktop = useIsDesktop()
    const [isTruncated, setIsTruncated] = useState(false)
    const [isTooltipOpen, setIsTooltipOpen] = useState(false)

    const debouncedClick = useDebouncedCallback(onClick)

    const isRemove = variant === "remove"
    const isInteractive = onClick !== undefined

    const boxClassName = clsx(
        styles.base,
        className,
        styles[`${variant}Variant`],
        styles[isDesktop ? "desktop" : "mobile"],
        {
            [styles.interactive]: isInteractive,
            [styles.fullWidth]: isFullWidth,
            [styles.isFocused]: _isFocused,
            [styles.isHovered]: _isHovered,
            [styles.isPressed]: isPressed,
            [styles.hasWarning]: isWarning,
        },
    )

    const renderContent = () => {
        if (isRemove) {
            return <Icon icon={REMOVE_ICON} size={12} container="fixedHeight" className={styles.icon} />
        } else {
            return (
                <>
                    {slotLeft && <span className={styles.slotLeft}>{slotLeft}</span>}
                    <TruncatableText
                        text={children}
                        className={styles.text}
                        // A static box can't take focus, so the text's own hover tooltip is enough there.
                        tooltipOnTruncate={!isInteractive}
                        tooltipPlacement="top"
                        onTruncationChange={setIsTruncated}
                    />
                    {isInteractive && (
                        <Icon
                            icon={CHEVRON_ICON}
                            size={10}
                            container="fixedHeight"
                            className={styles.icon}
                            containerClassName={styles.chevron}
                        />
                    )}
                </>
            )
        }
    }

    const renderBox = () => {
        if (isInteractive) {
            // On the button, so keyboard focus opens it too. Gated by `isOpen`: toggling `isDisabled` would
            // remount the button and re-measure its text in a loop.
            return (
                <Tooltip
                    body={isRemove ? REMOVE_LABEL : children}
                    isOpen={isTooltipOpen}
                    onOpenChange={(isOpen) => setIsTooltipOpen(isOpen && (isRemove || isTruncated))}
                    defaultPlacement="top"
                    wrapChildren={false}
                >
                    <button
                        ref={ref as Ref<HTMLButtonElement>}
                        type="button"
                        className={boxClassName}
                        onClick={noDebounce ? onClick : debouncedClick}
                        disabled={isDisabled}
                        aria-disabled={isDisabled}
                        aria-label={isRemove ? REMOVE_LABEL : undefined}
                        {...rest}
                    >
                        {renderContent()}
                    </button>
                </Tooltip>
            )
        } else {
            return (
                <span ref={ref as Ref<HTMLSpanElement>} className={boxClassName} {...rest}>
                    {renderContent()}
                </span>
            )
        }
    }

    return renderBox()
}

export default forwardRef(FilterChipBox)
